import Box from '@material-ui/core/Box';
import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import curry from 'lodash/curry';
import {
  ReactElement,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReactToPrint } from 'react-to-print';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import Modal from '~/ui/components/common/Modal';
import NoDataFound from '~/ui/components/common/NoDataFound';
import useRole from '../../../store/user/hooks/useRole';
import ClientInfo from '../../components/common/ClientInfo';
import Forms from '../Clients/reusable/HealthDetails/Forms';
import SimpleSelect from './components/SimpleSelect';

import useClientOptionsByTeams from '~/store/client/hooks/useClientOptionsByTeams';
import useQuery from '~/store/medicationLogistics/hooks/useQuery';
import { extractErrorMessage } from '~/utils/error/error';
import formatActTeamsOptions from '~/utils/formatActTeamsOptions';
import useQueryParam from './hooks/useQueryParam';

import { Grid } from '@material-ui/core';
import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import { ReactComponent as ReportDownloadIcon } from '~/ui/assets/images/reportDownload.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import { IDetails, ITypeDetails } from '../Clients/reusable/HealthDetails/types';
import ShowCurrentTeamVisitCheckbox from '../Visits/components/ShowCurrentTeamVisitCheckbox';
import styles from './MedicationLogistics.module.scss';

const MedicationDeliveryCalendar = lazy(() => import('./components/MedicationDeliveryCalendar'));
const MedicationDeliveryPreview = lazy(() => import('./components/MedicationDeliveryPreview'));

const MedicationLogistics = (): ReactElement => {
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openPreview, setOpenPreview] = useState(null);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string | null>(null);
  const [details, setDetails] = useState<IDetails | null>(null);

  const query = useQuery();
  const actTeamId = query.get('actTeamId');
  const clientId = query.get('clientId');
  const includeDeletedQuery = query.get('includeDeleted');

  const weekModeQuery = query.get('weekMode');
  const [weekMode, setWeekMode] = useState(weekModeQuery === 'true' || false);

  const user = useStoreState(state => state.user.current);
  const { availableActTeams } = useStoreState(state => state.actTeam);
  const { current, defaultClient } = useStoreState(state => state.client);
  const { setMedicationDelivery } = useStoreActions(actions => actions.medicationLogistics);
  const {
    items: availableClients,
    loading: clientsLoading,
    loadMore,
    setClientOptionsTeamIds,
  } = useClientOptionsByTeams(true, includeDeletedQuery === 'true');
  const { setDefaultClient, resetClientOptions, resetCurrentClient } = useStoreActions(
    actions => actions.client,
  );

  const currentMedicationDelivery = useStoreState(state => state.medicationLogistics.current);
  const deliveriesList = useStoreState(state => state.medicationLogistics.list);

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const onGetActTeamsAvailable = useStoreActions(actions => actions.actTeam.onGetActTeamsAvailable);
  const onGetMedicationDelivery = useStoreActions(
    actions => actions.medicationLogistics.onGetMedicationDelivery,
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const { getClientMultiTeamId } = useClientMultiTeam();

  const actTeamOptions = formatActTeamsOptions(availableActTeams);
  const availableTeamIds = useMemo(() => availableActTeams.map(t => t.id), [availableActTeams]);

  const { isLocalAdmin, isProgramAssistant, team: userTeam, isGlobalUser, isActTeam } = useRole();

  const showTeamSelect = isLocalAdmin || isProgramAssistant;

  const clientTeamId = getClientMultiTeamId(current?.actTeams);

  const initialTeamId =
    showTeamSelect || isGlobalUser ? clientTeamId || Number(actTeamId) : userTeam?.id;

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(initialTeamId || null);

  const effectiveTeamId =
    showTeamSelect || isGlobalUser ? selectedTeamId ?? initialTeamId : userTeam?.id;

  const teamId = String(effectiveTeamId);

  const onMount = useCallback(async () => {
    try {
      setLoading(true);

      if (showTeamSelect || isGlobalUser) {
        await Promise.all([
          onGetMyClinic(),
          onGetActTeamsAvailable({ clinicId: String(user.clinic.id) }),
        ]);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [
    onGetActTeamsAvailable,
    isGlobalUser,
    onGetMyClinic,
    showError,
    showTeamSelect,
    user.clinic.id,
  ]);

  const requestInfo = {
    clientId: String(current?.id),
    clinicId: String(user?.clinic.id),
    teamId,
  };

  const showSecondSubmit = !Object.keys(details || {}).length;

  const onAdd = curry((title: string, medicationId: number, date: string, injectable: boolean) => {
    setDetails({ medicationId, date, injectable });
    setModalTitle(title);
  });

  const onEdit = curry(
    (
      type: ITypeDetails,
      healthDetailsType: string,
      removeHealthDetailsName: boolean,
      id: number,
      healthDetailsName: string,
      isArchived: boolean,
      canEdit: boolean,
    ) => {
      if (isArchived || !canEdit) {
        setOpenPreview(true);
        setDetails({ type, id });
      } else {
        let title = `Edit “${healthDetailsName}” ${healthDetailsType}`;

        if (removeHealthDetailsName) {
          title = title.replace(/“[\w\s]*”/gi, '');
        }

        setModalTitle(title);
        setDetails({ type, id });
      }
      setLoadingDetails(true);
    },
  );

  const componentRef = useRef(null);

  const selectedInterval = query.get('selectedMonth')?.split('-');
  const documentTitle = `${current?.firstName}_${current?.lastName}_Delivery_data_${selectedInterval?.[0]}${selectedInterval?.[1]}${selectedInterval?.[2]}`;

  const printCalendar = useReactToPrint({
    content: () => componentRef.current,
    documentTitle,
  });

  const showCurrentTeamVisits = query.get('showCurrentTeamVisits') === 'true';

  const loadDetails = useCallback(async () => {
    try {
      if (details?.id) {
        const payload = {
          clinicId: String(user.clinic.id),
          clientId: String(current?.id),
          teamId,
          medicationDeliveryId: String(details?.id),
          currentTeamDeliveriesOnly: showCurrentTeamVisits,
        };

        await onGetMedicationDelivery(payload);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoadingDetails(false);
    }
  }, [
    current?.id,
    details?.id,
    showCurrentTeamVisits,
    onGetMedicationDelivery,
    showError,
    teamId,
    user.clinic.id,
  ]);

  const generatePDF = async (): Promise<void> => {
    setReportLoading(true);
    try {
      await api.medicationLogistics
        .getMedicationDeliveryReport(
          { clinicId: String(user.clinic.id), teamId },
          {
            clientId: String(current?.id),
            year: selectedInterval[0],
            month: selectedInterval[1],
            timeZoneOffset: new Date().getTimezoneOffset(),
            currentTeamDeliveriesOnly: showCurrentTeamVisits,
          },
        )
        .then((val: AxiosResponse<BlobPart, any>) => {
          const blob = new Blob([val.data]);
          const link = window.URL.createObjectURL(blob);
          saveAs(link, `${documentTitle}.pdf`);
          setReportLoading(false);
        });
    } catch (e) {
      showError(extractErrorMessage(e));
      setReportLoading(false);
    }
  };

  const exportDisabled = useMemo(
    () => deliveriesList?.length === 0 || weekMode,
    [deliveriesList?.length, weekMode],
  );

  const clientHasMultipleTeams = useMemo(
    () => (defaultClient || current)?.actTeams?.filter(item => !item.isArchived).length > 1,
    [defaultClient, current],
  );
  const isPastVisits =
    ((!clientId && !defaultClient?.id) || clientId === '-1') && (isActTeam || isGlobalUser);

  const showCurrentTeamVisitCheckbox = !isGlobalUser && !isPastVisits && clientHasMultipleTeams;

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    loadDetails();
  }, [details, loadDetails]);

  // global user role can see all clients from all assigned teams, so we pass all available team ids
  useEffect(() => {
    if (isGlobalUser) {
      setClientOptionsTeamIds(availableTeamIds);
      return;
    }
    if (selectedTeamId) {
      setClientOptionsTeamIds([selectedTeamId]);
    }
  }, [selectedTeamId, isGlobalUser, availableTeamIds, setClientOptionsTeamIds]);

  const { setQuery } = useQueryParam(
    'actTeamId',
    showTeamSelect && selectedTeamId ? String(selectedTeamId) : undefined,
  );

  const renderHeader = () => (
    <div className={styles.header}>
      <h2>Medication Logistics</h2>
      {showCurrentTeamVisitCheckbox && !showTeamSelect && <ShowCurrentTeamVisitCheckbox />}
    </div>
  );

  const handleSelectTeam = (value: number) => {
    setSelectedTeamId(value);
    // During changing team we need to reset client options and default client, because in store is stored current selected client from previous team
    resetClientOptions();
    resetCurrentClient();
    setDefaultClient(null);
    setQuery('clientId', '');
  };

  if (loading || !user) return <Loader />;

  return (
    <Suspense fallback={<Loader />}>
      <div className={styles.container}>
        {showTeamSelect && renderHeader()}
        <Box className={styles.titleSection}>
          {showTeamSelect ? (
            <>
              <SimpleSelect
                label="Assigned Team"
                value={selectedTeamId}
                setValue={handleSelectTeam}
                options={actTeamOptions}
              />
              {showCurrentTeamVisitCheckbox && (
                <Grid
                  item
                  sm={5}
                  {...(showCurrentTeamVisitCheckbox && showTeamSelect ? { sm: 3 } : {})}
                >
                  <ShowCurrentTeamVisitCheckbox label="Show Current Team's Medication Deliveries Only" />
                </Grid>
              )}
            </>
          ) : (
            renderHeader()
          )}

          {!!teamId && !!availableClients.length && (
            <Box className={styles.exportButtons}>
              <Button
                variant="outlined"
                color="primary"
                disabled={exportDisabled}
                onClick={() => printCalendar()}
                className={styles.printCalendarButton}
              >
                Export Calendar
              </Button>
              <Button
                isLoading={reportLoading}
                loaderColor="white"
                variant="contained"
                color="primary"
                startIcon={
                  <ReportDownloadIcon
                    fill={exportDisabled ? variables.colorWhite : variables.colorWhite}
                  />
                }
                disabled={exportDisabled}
                onClick={() => generatePDF()}
              >
                Export PDF
              </Button>
            </Box>
          )}
        </Box>

        {!!(selectedTeamId || isGlobalUser) &&
          (availableClients.length ? (
            <ClientInfo
              clientId={
                availableClients.find(c => c.id === (defaultClient?.id || Number(clientId)))?.id
              }
              teamId={selectedTeamId}
              loading={clientsLoading}
              loadMore={loadMore}
            />
          ) : (
            <Box className={styles.centered}>
              <NoDataFound title="No clients assigned to the team" waitBeforeShow={3000} />
            </Box>
          ))}
        {!!(selectedTeamId || isGlobalUser) && !!availableClients.length && !!current && (
          <MedicationDeliveryCalendar
            teamId={Number(teamId)}
            weekMode={weekMode}
            setWeekMode={setWeekMode}
            onAdd={onAdd('Add Medication Delivery')}
            onEdit={onEdit('medicationDelivery', 'Medication Delivery', true)}
            componentRef={componentRef}
            showCurrentTeamVisits={showCurrentTeamVisits}
          />
        )}
        {/* Preview modal */}
        {(openPreview || (details && details.id)) && !modalTitle && (
          <Modal
            className={styles.modal}
            open
            onClose={() => {
              setOpenPreview(null);
              setDetails(null);
              setMedicationDelivery(null);
            }}
          >
            <MedicationDeliveryPreview
              medicationDelivery={currentMedicationDelivery}
              onClose={() => {
                setOpenPreview(null);
                setDetails(null);
                setMedicationDelivery(null);
              }}
            />
          </Modal>
        )}
        {/* Edit modals */}
        {(modalTitle || (details && details.id)) && !openPreview && (
          <Modal
            className={styles.modal}
            open
            onClose={() => {
              setModalTitle(null);
              setDetails(null);
            }}
          >
            <div className={styles.modalWrapper}>
              <h3 className={styles.modalTitle}>{modalTitle}</h3>
              {loadingDetails ? (
                <Loader />
              ) : (
                <Forms
                  formType={
                    currentMedicationDelivery?.medication?.injectable || details?.injectable
                      ? 'injectableMedicationDelivery'
                      : 'medicationDelivery'
                  }
                  setModalTitle={setModalTitle}
                  setDetails={setDetails}
                  requestInfo={requestInfo}
                  details={details}
                  showSecondSubmit={showSecondSubmit}
                  showCurrentTeamVisits={showCurrentTeamVisits}
                  current={
                    currentMedicationDelivery || {
                      medicationIds: [details.medicationId],
                      date: details.date,
                      teamMember: !showTeamSelect
                        ? { id: user.id, name: user.fullName }
                        : undefined,
                    }
                  }
                  primaryDiagnosis={null}
                />
              )}
            </div>
          </Modal>
        )}
      </div>
    </Suspense>
  );
};

export default MedicationLogistics;
